import type { DiceRoll } from "@dice-roller/rpg-dice-roller";
import { z } from "zod";
import type { DiceRollConfig } from "@/lib/utils/dice/notation";

export const DiceRollConfigSchema = z.object({
  count: z.number().int().min(1).max(100).optional(),
  sides: z.number().int().min(1).max(1000),
  modifier: z.number().int().min(-1000).max(1000).optional(),
  keep: z.enum(["highest", "lowest"]).optional(),
  // generic rpg-dice-roller cs/cf passthrough (cs>=N / cscf) — works on any
  // die; NOT a d20 assumption.
  critThreshold: z.number().int().min(1).max(100).optional(),
  critMarkers: z.boolean().optional(),
  rerollOnceOn: z.number().int().min(1).max(100).optional(),
  dieFloor: z.number().int().min(1).max(1000).optional(),
  totalFloor: z.number().int().min(-1000).max(1000).optional(),
  maximize: z.boolean().optional(),
  // LAF-261: additive bonus-dice terms (e.g. bless's +1d4) folded into the
  // rolled config so the resolver's dice.roll(diceConfig) carries them.
  // LAF-254: `subtract: true` marks a term as subtractive (e.g. bane's −1d4).
  bonusDice: z
    .array(
      z.object({
        count: z.number().int().min(1).max(100),
        sides: z.number().int().min(1).max(1000),
        subtract: z.boolean().optional(),
      })
    )
    .optional(),
});

export type DiceProvider = {
  /**
   * Rolls dice based on the provided configuration.
   * @param config The configuration for the dice roll (sides, count, modifiers, etc.)
   * @returns The resulting DiceRoll object.
   */
  roll(config: DiceRollConfig): DiceRoll;
};
